import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, KeyRound, RefreshCw, CheckCircle2, AlertTriangle, Mail, ShieldAlert } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Settings → Courier Passwords.
 *
 * Delhivery B2B passwords expire every few months. When that happens every booking, pickup, POD and
 * appointment for that courier stops, and (before this) the platform kept re-trying the dead password
 * until Delhivery blocked us — 1,072 logins in three hours on 23-09-2026.
 *
 * It now repairs itself: the refusal carries a one-time reset link, so a new password is set, proved
 * with a real login and saved, usually within three minutes. This screen is for watching that happen
 * and for stepping in on the rare occasion it cannot.
 */
interface Account {
  username: string;
  status: 'EXPIRED' | 'RESET_REQUESTED' | 'RESOLVED' | 'FAILED' | 'RATE_LIMITED' | 'BLOCKED';
  packId: number | null;
  courierIds: number[];
  detectedAt?: string;
  requestedAt?: string;
  resolvedAt?: string;
  lastCheckAt?: string;
  lastError?: string;
  lockedUntil?: string;
  note?: string;
}

const LOOK: Record<string, { text: string; chip: string; Icon: typeof KeyRound; says: string }> = {
  RESOLVED: { text: 'Working', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2, says: 'The password was replaced and checked — this courier is live.' },
  EXPIRED: { text: 'Password expired', chip: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle, says: 'Delhivery has expired this password. A new one is being set automatically.' },
  RESET_REQUESTED: { text: 'Reset in progress', chip: 'bg-blue-50 text-blue-700 border-blue-200', Icon: RefreshCw, says: 'A new password has been asked for. It is applied as soon as it arrives.' },
  RATE_LIMITED: { text: 'Delhivery is making us wait', chip: 'bg-slate-100 text-slate-700 border-slate-200', Icon: ShieldAlert, says: 'Too many login tries. Nothing is sent until the wait is over — this clears by itself.' },
  BLOCKED: { text: 'Blocked by Delhivery', chip: 'bg-rose-50 text-rose-700 border-rose-200', Icon: ShieldAlert, says: 'Delhivery is refusing our logins for the moment. It clears by itself.' },
  FAILED: { text: 'Needs a hand', chip: 'bg-rose-50 text-rose-700 border-rose-200', Icon: AlertTriangle, says: 'The automatic reset did not go through — use the buttons below.' },
};

const when = (v?: string) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export function CourierPasswordsPanel() {
  const qc = useQueryClient();
  const [typed, setTyped] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['courier-passwords'],
    queryFn: async () => (await api.get('/courier-passwords/delhivery')).data as { mailbox: { user: string } | null; accounts: Account[] },
    refetchInterval: 30_000,
  });

  const after = (r: { ok?: boolean; message?: string }, fallback: string) => {
    if (r?.ok === false) toast.error(r.message || 'Delhivery did not accept that.');
    else toast.success(r?.message || fallback);
    qc.invalidateQueries({ queryKey: ['courier-passwords'] });
  };
  const failed = (e: { response?: { data?: { message?: string } } }) =>
    toast.error(e?.response?.data?.message || 'Could not reach Delhivery.');

  const resetNow = useMutation({
    mutationFn: async (username: string) => (await api.post('/courier-passwords/delhivery/reset-now', { username })).data,
    onSuccess: (r) => after(r, 'A new password was set and checked.'),
    onError: failed,
  });
  const askEmail = useMutation({
    mutationFn: async (username: string) => (await api.post('/courier-passwords/delhivery/request-reset', { username, force: true })).data,
    onSuccess: (r) => after(r, 'Delhivery has been asked to email a new password.'),
    onError: failed,
  });
  const checkMail = useMutation({
    mutationFn: async (username: string) => (await api.post('/courier-passwords/delhivery/check-mailbox', { username })).data,
    onSuccess: (r) => after(r, 'Mailbox checked.'),
    onError: failed,
  });
  const savePwd = useMutation({
    mutationFn: async (v: { username: string; password: string }) => (await api.post('/courier-passwords/delhivery/set-password', v)).data,
    onSuccess: (r) => after(r, 'Password saved.'),
    onError: failed,
  });
  const busy = resetNow.isPending || askEmail.isPending || checkMail.isPending || savePwd.isPending;

  const accounts = data?.accounts ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><KeyRound size={18} /> Courier Passwords</h3>
        <p className="text-sm text-slate-500 mt-1">
          Delhivery expires their B2B password every few months, which stops bookings, pickups, PODs and appointments
          for that courier. The platform now notices, sets a new password and checks it — normally within three minutes.
          Nothing here needs doing unless a row says so.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : accounts.length === 0 ? (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 text-sm text-emerald-800 flex items-start gap-2">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <div>
            <b>All courier passwords are fine.</b>
            <div className="text-emerald-700 mt-0.5">A courier only appears here once Delhivery refuses its password. An empty list is good news.</div>
          </div>
        </div>
      ) : (
        accounts.map((a) => {
          const look = LOOK[a.status] ?? LOOK.FAILED;
          const waiting = Boolean(a.lockedUntil && new Date(a.lockedUntil) > new Date());
          return (
            <div key={a.username} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
              <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 justify-between">
                <div>
                  <div className="font-semibold text-slate-800">{a.username}</div>
                  <div className="text-xs text-slate-500">Credential pack {a.packId ?? '—'} · courier {a.courierIds?.join(', ') || '—'}</div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 ${look.chip}`}>
                  <look.Icon size={13} /> {look.text}
                </span>
              </div>

              <div className="px-4 py-3 space-y-3">
                <p className="text-sm text-slate-600">{look.says}</p>

                <div className="grid sm:grid-cols-4 gap-3 text-xs">
                  <div><div className="text-slate-400">First noticed</div><div className="text-slate-700">{when(a.detectedAt)}</div></div>
                  <div><div className="text-slate-400">New one asked for</div><div className="text-slate-700">{when(a.requestedAt)}</div></div>
                  <div><div className="text-slate-400">Last check</div><div className="text-slate-700">{when(a.lastCheckAt)}</div></div>
                  <div><div className="text-slate-400">Fixed at</div><div className="text-slate-700">{when(a.resolvedAt)}</div></div>
                </div>

                {waiting && (
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-600">
                    Nothing is being sent to Delhivery until <b>{when(a.lockedUntil)}</b>. This is deliberate — re-trying a
                    dead password is what gets us blocked.
                  </div>
                )}
                {a.lastError && a.status !== 'RESOLVED' && (
                  <div className="text-xs bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-rose-700 break-all">
                    Delhivery said: {a.lastError}
                  </div>
                )}

                {a.status !== 'RESOLVED' && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      onClick={() => resetNow.mutate(a.username)}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {resetNow.isPending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                      Set a new password now
                    </button>
                    <button
                      onClick={() => askEmail.mutate(a.username)}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-md border border-slate-300 text-xs flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Mail size={13} /> Ask Delhivery to email one
                    </button>
                    <button
                      onClick={() => checkMail.mutate(a.username)}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-md border border-slate-300 text-xs flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <Mail size={13} /> Look for their email now
                    </button>
                  </div>
                )}

                <div className="pt-2 border-t border-slate-100">
                  <div className="text-xs text-slate-500 mb-1.5">
                    Got the password from Delhivery by email or phone? Type it here — it is tried against Delhivery
                    first, and only saved if it works.
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={typed[a.username] ?? ''}
                      onChange={(e) => setTyped((t) => ({ ...t, [a.username]: e.target.value }))}
                      placeholder="New password from Delhivery"
                      className="flex-1 max-w-sm px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                    />
                    <button
                      onClick={() => savePwd.mutate({ username: a.username, password: typed[a.username] ?? '' })}
                      disabled={busy || !(typed[a.username] ?? '').trim()}
                      className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-medium disabled:opacity-50"
                    >
                      Check &amp; save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
        Mailbox used when Delhivery emails a password:{' '}
        {data?.mailbox ? <b>{data.mailbox.user}</b> : <span className="text-amber-600">none set up</span>}. This is only a
        fallback — the usual route needs no email at all.
      </div>
    </div>
  );
}
